import {getTestProps} from '@benchling/js-test-utils';
import {Button, createStyles, Group} from '@mantine/core';
import React, {useState} from 'react';

import {Section} from 'constants/NavConstants';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import {ButtonStyle} from 'ui-library/buttons/Button';
import useRecentWorklistsQuery from 'worklists/useRecentWorklistsQuery';
import {WorklistType} from 'worklists/WorklistConstants';
import WorklistCreateButton from 'worklists/WorklistCreateButton';
import {openWorklistListing} from 'worklists/WorklistUIActions';

import CardSection, {
  CardSectionMessageRow,
  CardSectionRowList,
  CardSectionRowProps,
  renderQueryLoadingOrError,
} from './CardSection';
import {
  HOMEPAGE_SECTION_TEST_COMPONENT,
  INITIAL_ROWS_TO_DISPLAY,
  MAX_ROWS_TO_DISPLAY,
} from './HomepageConstants';
import {getHomepageWorklistOutlineIcon} from './HomepageIconUtil';
import {trackHomepageEvent} from './HomepageUtils';

const TEST_COMPONENT = HOMEPAGE_SECTION_TEST_COMPONENT.WORKLISTS;

const WORKLIST_TYPE_TO_LABEL = {
  [WorklistType.CONTAINER]: 'Container',
  [WorklistType.BATCH]: 'Batch',
  [WorklistType.BIOENTITY]: 'Entity',
  [WorklistType.PLATE]: 'Plate',
};

const useStyles = createStyles((theme) => ({
  worklistCreateButton: {
    '&&': {
      borderRadius: theme.radius.md,
    },
  },
}));

export default function WorklistsSection(): JSX.Element {
  const {queryResultType, recentWorklists} = useRecentWorklistsQuery();
  const [showMore, setShowMore] = useState(false);
  const {classes} = useStyles();
  const worklistsSectionRows: Array<CardSectionRowProps> | undefined = recentWorklists
    ?.slice(0, showMore ? MAX_ROWS_TO_DISPLAY : INITIAL_ROWS_TO_DISPLAY)
    .map((worklist) => ({
      id: worklist.id,
      icon: getHomepageWorklistOutlineIcon(worklist.itemType as WorklistType),
      name: worklist.name,
      modifiedAt: worklist.modifiedAt,
      count: worklist.items.length,
      onClick: () => {
        trackHomepageEvent('recent_worklist_clicked');
        openWorklistListing(worklist);
      },
      rightContent: WORKLIST_TYPE_TO_LABEL[worklist.itemType as WorklistType],
    }));
  const loadingOrError = renderQueryLoadingOrError(
    {type: queryResultType},
    'Error loading worklists',
    TEST_COMPONENT
  );
  return (
    <CardSection
      title="Worklists"
      dataTestAttrs={{component: TEST_COMPONENT, element: 'root'}}
      rightContent={
        <Group>
          <WorklistCreateButton
            buttonStyle={ButtonStyle.TERTIARY_BLUE_POWDER}
            className={classes.worklistCreateButton}
          />{' '}
          <Button
            size="sm"
            // eslint-disable-next-line no-restricted-syntax -- no custom variant matches the design (yet)
            variant="default"
            color="gray"
            onClick={() => {
              SidepanelActions.openSection(Section.WORKLISTS);
            }}
            {...getTestProps({component: TEST_COMPONENT, element: 'seeAllButton'})}
          >
            See all
          </Button>
        </Group>
      }
    >
      {loadingOrError}
      {queryResultType === QueryResultType.SUCCESS &&
        (!worklistsSectionRows!.length ? (
          <CardSectionMessageRow
            message="No recent worklists"
            dataTestAttrs={{component: TEST_COMPONENT, element: 'messageRow', key: 'empty'}}
          />
        ) : (
          <CardSectionRowList rows={worklistsSectionRows!} />
        ))}
      {recentWorklists != null && recentWorklists.length > INITIAL_ROWS_TO_DISPLAY && (
        <CardSectionMessageRow
          message={showMore ? 'Show less' : 'Show more'}
          onClick={() => {
            setShowMore(!showMore);
          }}
        />
      )}
    </CardSection>
  );
}
